<script setup lang="ts">
import { useChapterHeadings } from "../composables/useChapterHeadings";
import { trimmedMaybe } from "../lib/trimmedString";
import {
  isJust,
  isNothing,
  just,
  matchMaybe,
  nothing,
  fromMaybe,
  toMaybe,
  type Maybe,
} from "../lib/maybe";
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  useTemplateRef,
  watch,
} from "vue";
import { storeToRefs } from "pinia";
import { useRoute, useRouter } from "vue-router";
import { listen, type UnlistenFn } from "@tauri-apps/api/event";
import EditorOutlinePane from "./EditorOutlinePane.vue";
import OutlineSurface from "./OutlineSurface.vue";
import EditorStatusBar from "./EditorStatusBar.vue";
import ProjectSearchDialog from "./ProjectSearchDialog.vue";
import ThesaurusDialog from "./ThesaurusDialog.vue";
import KeyboardShortcutsDialog from "./KeyboardShortcutsDialog.vue";
import AboutPanel from "./AboutPanel.vue";
import { aboutHostFor } from "./AboutPanel.update";
import { openAboutWindow } from "../lib/tauri/about";
import ZwButton from "./ui/ZwButton.vue";
import ZwDialog from "./ui/ZwDialog.vue";
import ZwInput from "./ui/ZwInput.vue";
import ZwMenu from "./ui/ZwMenu.vue";
import ZwMenuItem from "./ui/ZwMenuItem.vue";
import ZwContextMenuHost from "./ui/ZwContextMenuHost.vue";
import { useEditorFont } from "../composables/useEditorFont";
import { useShellWidth } from "../composables/useShellWidth";
import { createProjectExport } from "../composables/useProjectExport";
import { navigateToProjectTab } from "../lib/editorTabs";
import { openDemoProject } from "../lib/demo";
import { bindProjectRouter } from "../lib/projectRouter";
import { onProjectDictionaryChange } from "../lib/projectDictionary";
import { assertNever } from "../lib/assertNever";
import { formatErrorMessage } from "../lib/projectAssets";
import { useTea } from "../lib/tea";
import { firstManuscriptScenePath, type ZwFonts } from "../lib/zwConfig";
import {
  initialEditorShellModel,
  updateEditorShellFn,
  type EditorShellCmd,
} from "./EditorShell.update";
import {
  openCloudEditorProject,
  openShareReaderProject,
} from "../lib/platform";
import {
  cloudAccountUrl,
  cloudSignInUrl,
  isCloudEditorHosted,
  isCloudReaderHosted,
  activeCloudProjectRoot,
  resolvedCloudProjectId,
} from "../lib/cloudEditorHost";
import {
  watchHostedEditorTips,
  type StopWatchHostedEditorTips,
} from "../lib/cloudRealtimeWeb";
import { pathFromLegacyActiveTab } from "../router/projectPaths";
import { installRouteDocumentSync } from "../composables/useRouteDocumentSync";
import {
  createNewProject,
  pickProjectParentFolder,
  readLastRoute,
  reloadEditorFromDisk,
} from "../lib/tauri/project";
import {
  getEditorPayload,
  isMobileShell,
  navigateToSplash,
  type EditorActiveTab,
  type EditorPayload,
} from "../lib/tauri/core";
import { useProjectConversionStore } from "../stores/useProjectConversionStore";
import ProjectConversionNotice from "./ProjectConversionNotice.vue";
import ExportToast from "./ExportToast.vue";
import { isTauri } from "../lib/runtime";
import { applyPayload } from "../stores/editorPayload";
import { useEditorChromeStore } from "../stores/useEditorChromeStore";
import { useEditorCloudStore } from "../stores/useEditorCloudStore";
import { useEditorHeadingsStore } from "../stores/useEditorHeadingsStore";
import { useEditorSaveStore } from "../stores/useEditorSaveStore";
import { useEditorSessionStore } from "../stores/useEditorSessionStore";
import { useDisplayModeStore } from "../stores/useDisplayModeStore";
import { useShareModeStore } from "../stores/useShareModeStore";
import { useOutlineActionsStore } from "../stores/useOutlineActionsStore";
import { useTimelineMapsStore } from "../stores/useTimelineMapsStore";
import { useOutlineEntitiesStore } from "../stores/useOutlineEntitiesStore";
import { useProjectGoalsStore } from "../stores/useProjectGoalsStore";
import { useProjectSettingsStore } from "../stores/useProjectSettingsStore";
import { useProjectWorkspaceStore } from "../stores/useProjectWorkspaceStore";
import { listenMenuCommands } from "../lib/menuCommand";
import { applyEditHistory } from "../lib/editHistoryMenu";
import {
  EDIT_REDO_MENU_ID,
  EDIT_UNDO_MENU_ID,
  type EditHistoryKind,
} from "../lib/editHistorySurface";
import { openShareViaCloud } from "../lib/cloudShare";
import { useWordLookupStore } from "../stores/useWordLookupStore";
import { useStoryboardDragStore } from "../stores/useStoryboardDragStore";
import type { EditorPortHandle } from "../editor/port";
import { modalDialogOpen } from "../lib/modalDialog";

const props = withDefaults(
  defineProps<{
    /** Browser/dev fallback when not running inside Tauri. */
    demo?: boolean;
  }>(),
  { demo: false },
);

const router = useRouter();
const route = useRoute();

const session = useEditorSessionStore();
// Instantiated here (not from `EditorOutlinePane.vue`) so sibling subtrees
// like `DocumentEditor.vue` — e.g. `EntityFieldsPanel.vue`, which refreshes
// it after saving an entry's fields — can reach the same live list. Watches
// `rootPath` and keeps sidecar entity lists in sync.
useOutlineEntitiesStore();
useTimelineMapsStore();
const display = useDisplayModeStore();
const share = useShareModeStore();
const outlineActions = useOutlineActionsStore();
const chrome = useEditorChromeStore();
const headings = useEditorHeadingsStore();
const projectGoals = useProjectGoalsStore();
const projectSettings = useProjectSettingsStore();
const save = useEditorSaveStore();
const cloud = useEditorCloudStore();
const conversion = useProjectConversionStore();
const workspace = useProjectWorkspaceStore();
const wordLookup = useWordLookupStore();
// Instantiated here (not inside `StoryboardBoard.vue`) so the sidebar's
// Unplaced panel — a router-view sibling of the board — joins the same drag.
const storyboardDrag = useStoryboardDragStore();

const editor = useTemplateRef<EditorPortHandle>("editor");
/** Maybe view of the template ref (Nothing until the editor mounts). */
const editorMaybe = computed(() => toMaybe(editor.value));

/** Use the mounted editor to seed the dialog; otherwise open an empty lookup. */
function openThesaurusUi(): void {
  if (display.isWriting === false) return;
  wordLookup.openFromEditor(nothing);
  session.thesaurusDialogOpen = true;
  const view = toMaybe(editor.value);
  if (isJust(view)) {
    const open = toMaybe(view.value.openThesaurus);
    if (isJust(open)) {
      open.value();
      return;
    }
  }
}

function closeThesaurusUi(): void {
  session.thesaurusDialogOpen = false;
}

watch(
  () => display.isWriting,
  (writing) => {
    if (writing === false) closeThesaurusUi();
  },
);

// Status bar is a sibling of the routed main content, so chrome is registered
// here (Pinia stores are global — not limited to descendant components).
chrome.register({
  toggleHistory: () => editor.value?.toggleHistory?.(),
  toggleComments: () => editor.value?.toggleComments?.(),
  closeHistory: () => editor.value?.closeHistory?.(),
  openFind: () => editor.value?.openFind?.(),
  findNext: () => editor.value?.findNext?.(),
  selectNextOccurrence: () => editor.value?.selectNextOccurrence?.(),
  openThesaurus: () => openThesaurusUi(),
});

const { bodyFontId, headingFontId, setBodyFont, setHeadingFont } =
  useEditorFont();

projectSettings.registerApplyFonts((fonts: ZwFonts) => {
  setBodyFont(fonts.body, false);
  setHeadingFont(fonts.heading, false);
});
projectSettings.registerOnSettingsSaved(headings.scheduleHarperLexiconRefresh);

/**
 * Convert the project to `.zwdoc` first if it still has Markdown documents
 * (#259), returning a reloaded payload when it did. Share sessions never
 * convert: the owner's next open does. On failure the payload comes back
 * unchanged and `ProjectConversionNotice` explains what stopped it.
 */
async function withConvertedProject(
  payload: EditorPayload,
): Promise<EditorPayload> {
  if (isNothing(payload.rootPath) || share.shareMode || isCloudReaderHosted()) {
    return payload;
  }
  const root = payload.rootPath.value;
  try {
    const converted = await conversion.convertIfNeeded(root);
    return converted ? await reloadEditorFromDisk(root) : payload;
  } catch (error) {
    console.error("Failed to check documents for conversion", error);
    return payload;
  }
}

async function reloadHostedFromCloud(): Promise<void> {
  const payload = await getEditorPayload();
  applyPayload(payload);
  await headings.refreshEntityOccurrences();
}

function startHostedRealtimeWatch(): void {
  if (isCloudEditorHosted() === false) return;
  const projectId = resolvedCloudProjectId(session.rootPath);
  if (isNothing(projectId)) return;

  matchMaybe(stopHostedRealtime, {
    just: (stop) => {
      stop();
    },
    nothing: () => {},
  });
  stopHostedRealtime = just(
    watchHostedEditorTips({
      projectId: fromMaybe(projectId) ?? "",
      shouldDefer: () => save.saving || session.dirty,
      onSynced: reloadHostedFromCloud,
    }),
  );
}

const exportApi = createProjectExport({ bodyFontId, headingFontId });

bindProjectRouter(router);
workspace.registerEditor(editorMaybe);
workspace.registerNavigateToTab((tab) => {
  void navigateToProjectTab(tab);
});

const stopRouteSync = installRouteDocumentSync(router);
onUnmounted(() => stopRouteSync());
onUnmounted(() => storyboardDrag.cleanup());

// Keep session.storyboardView in sync for callers that still read the flag.
watch(
  () => [route.name, session.isProject] as const,
  ([name, isProject]) => {
    session.storyboardView = isProject && name === "storyboard";
  },
  { immediate: true },
);

watch(
  () => session.historyOpen,
  (open) => {
    if (open) session.goalsOpen = false;
  },
);

watch(
  () => projectGoals.primaryGoalChip,
  (chip) => {
    if (isNothing(toMaybe(chip))) session.goalsOpen = false;
  },
);

function higherPriorityDismissOpen(): boolean {
  if (modalDialogOpen() === true) {
    return true;
  }
  if (document.querySelector('.zw-menu [aria-expanded="true"]') !== null) {
    return true;
  }
  return false;
}

/** Menu accelerator + window keydown can both fire; ignore the twin. */
let lastFocusToggleAt = 0;
function toggleFocusModeGuarded(): void {
  if (display.focusModeEligible === false && display.isFocus === false) return;
  const now = Date.now();
  if (now - lastFocusToggleAt < 250) return;
  lastFocusToggleAt = now;
  display.toggleFocusMode();
}

let lastReadingToggleAt = 0;
function toggleReadingModeGuarded(): void {
  if (share.shareMode === true) return;
  if (display.readingModeEligible === false && display.isReading === false) {
    return;
  }
  const now = Date.now();
  if (now - lastReadingToggleAt < 250) return;
  lastReadingToggleAt = now;
  display.toggleReadingMode();
}

/** Outline ribbon order — ⌘1…⌘5 / Ctrl+1…5. */
const SECTION_DIGIT_TABS: Record<string, EditorActiveTab> = {
  "1": "manuscript",
  "2": "storyboard",
  "3": "notes",
  "4": "database",
  "5": "trash",
};

const VIEW_MENU_TABS: Record<string, EditorActiveTab> = {
  "view-manuscript": "manuscript",
  "view-storyboard": "storyboard",
  "view-notes": "notes",
  "view-database": "database",
  "view-trash": "trash",
};

/** Menu accelerator + window keydown can both fire; ignore the twin. */
let lastShortcutsToggleAt = 0;

const newProjectInput = useTemplateRef<{
  focus: () => void;
  select: () => void;
}>("newProjectInput");

async function runShellCmd(cmd: EditorShellCmd): Promise<void> {
  switch (cmd.scope) {
    case "newProject": {
      const createCmd = cmd.cmd;
      switch (createCmd.kind) {
        case "create": {
          if (isTauri() === false) {
            void dispatchShell({
              type: "NewProject",
              msg: { type: "CreateAborted" },
            });
            return;
          }
          try {
            const parent = await pickProjectParentFolder();
            const parentPath = trimmedMaybe(parent);
            if (isNothing(parentPath)) {
              void dispatchShell({
                type: "NewProject",
                msg: { type: "CreateAborted" },
              });
              return;
            }
            // Keep the current editor open; Open Project from the editor does the same.
            await createNewProject(createCmd.name, parentPath.value, false);
            void dispatchShell({
              type: "NewProject",
              msg: { type: "CreateSucceeded" },
            });
          } catch (error) {
            console.error("Failed to create project", error);
            void dispatchShell({
              type: "NewProject",
              msg: {
                type: "CreateFailed",
                error: formatErrorMessage(error, "Could not create project."),
              },
            });
          }
          return;
        }
        default:
          assertNever(createCmd as never);
      }
      return;
    }
    case "about":
      try {
        await openAboutWindow(cmd.cmd.check);
      } catch (error) {
        console.error("Failed to open About window", error);
      }
      return;
    default:
      assertNever(cmd as never);
  }
}

const { model: shellModel, dispatch: dispatchShell } = useTea(
  initialEditorShellModel,
  updateEditorShellFn,
  { runCmd: just(runShellCmd) },
);

const aboutOpen = computed((): boolean => shellModel.value.about.open);
const aboutCheckOnOpen = computed(
  (): boolean => shellModel.value.about.checkOnOpen,
);

function openAbout(): void {
  void dispatchShell({ type: "About", msg: { type: "Open", check: false } });
}

function openAboutAndCheck(): void {
  void dispatchShell({ type: "About", msg: { type: "Open", check: true } });
}

function closeAbout(): void {
  void dispatchShell({ type: "About", msg: { type: "Close" } });
}

const shortcutsOpen = computed({
  get: (): boolean => shellModel.value.shortcutsOpen,
  set: (open: boolean): void => {
    void dispatchShell({ type: "SetShortcutsOpen", open });
  },
});

const creatingProject = computed(() => shellModel.value.newProject.open);
const creatingProjectBusy = computed(() => shellModel.value.newProject.busy);
const newProjectError = computed(() => shellModel.value.newProject.error);
const newProjectName = computed({
  get: (): string => shellModel.value.newProject.draft,
  set: (draft: string): void => {
    void dispatchShell({
      type: "NewProject",
      msg: { type: "DraftChanged", draft },
    });
  },
});

const newProjectDescription = computed(() =>
  session.nativeMobileShell
    ? "Name your project. It will be saved in this app."
    : "Name it, then choose a folder.",
);
const newProjectPrimaryLabel = computed(() => {
  if (creatingProjectBusy.value) return "Creating…";
  return session.nativeMobileShell ? "Create" : "Choose location…";
});

function openNewProjectDialog(): void {
  if (creatingProjectBusy.value || props.demo || isTauri() === false) return;
  void dispatchShell({ type: "NewProject", msg: { type: "Open" } });
  void nextTick(() => {
    newProjectInput.value?.focus();
    newProjectInput.value?.select();
  });
}

function cancelNewProject(): void {
  void dispatchShell({ type: "NewProject", msg: { type: "Cancel" } });
}

function commitNewProject(): void {
  void dispatchShell({ type: "NewProject", msg: { type: "Commit" } });
  // Empty-name Commit updates the model sync (no Cmds) before this runs.
  if (shellModel.value.newProject.error.length > 0) {
    newProjectInput.value?.focus();
  }
}

/** Edit > Undo / Redo. A focused text field uses its own history. */
function runFocusedEditorHistory(kind: EditHistoryKind): void {
  applyEditHistory(kind, () => {
    const port = toMaybe(editor.value);
    if (isNothing(port)) return;
    const run = toMaybe(port.value.runCommand);
    if (isNothing(run)) return;
    run.value(kind);
  });
}

function openKeyboardShortcuts(): void {
  if (share.shareMode === true) return;
  const now = Date.now();
  if (now - lastShortcutsToggleAt < 250) return;
  lastShortcutsToggleAt = now;
  void dispatchShell({ type: "SetShortcutsOpen", open: true });
}

function toggleKeyboardShortcuts(): void {
  if (share.shareMode === true) return;
  const now = Date.now();
  if (now - lastShortcutsToggleAt < 250) return;
  lastShortcutsToggleAt = now;
  if (shellModel.value.shortcutsOpen) {
    void dispatchShell({ type: "SetShortcutsOpen", open: false });
    return;
  }
  if (higherPriorityDismissOpen()) return;
  void dispatchShell({ type: "SetShortcutsOpen", open: true });
}

function onKeydown(event: KeyboardEvent): void {
  const key = event.key.toLowerCase();
  const mod = event.metaKey || event.ctrlKey;

  if (key === "escape") {
    if (higherPriorityDismissOpen() || event.defaultPrevented) return;
    if (display.isReading === true && share.shareMode === false) {
      event.preventDefault();
      display.exitReadingMode();
      return;
    }
    if (display.isFocus) {
      event.preventDefault();
      display.exitFocusMode();
      return;
    }
  }

  // Capture so Focus / Reading still toggle when the editor or the browser
  // would otherwise swallow the chord.
  if (mod && event.shiftKey && key === "f") {
    if (display.focusModeEligible === false && display.isFocus === false) {
      return;
    }
    if (higherPriorityDismissOpen()) return;
    event.preventDefault();
    toggleFocusModeGuarded();
    return;
  }
  if (mod && event.shiftKey && key === "r") {
    if (display.readingModeEligible === false && display.isReading === false) {
      return;
    }
    if (higherPriorityDismissOpen()) return;
    event.preventDefault();
    toggleReadingModeGuarded();
  }
}

function onKeydownBubble(event: KeyboardEvent): void {
  const key = event.key.toLowerCase();
  const mod = event.metaKey || event.ctrlKey;

  // New project (⌘⇧N / Ctrl+Shift+N). Plain Mod+N stays "New" in the current section.
  if (mod && event.shiftKey && event.altKey === false && key === "n") {
    if (higherPriorityDismissOpen()) return;
    event.preventDefault();
    openNewProjectDialog();
    return;
  }

  // Project search (⌘⇧P / Ctrl+Shift+P). Leave plain Mod-P for future Print.
  if (mod && event.shiftKey && event.altKey === false && key === "p") {
    if (share.shareMode === true) return;
    if (session.projectSearchOpen) {
      event.preventDefault();
      session.projectSearchOpen = false;
      return;
    }
    if (higherPriorityDismissOpen()) return;
    event.preventDefault();
    session.projectSearchOpen = true;
    return;
  }

  // Thesaurus (⌘⇧Y / Ctrl+Shift+Y). Writing mode only; toggle the dialog.
  if (mod && event.shiftKey && event.altKey === false && key === "y") {
    if (display.isWriting === false) return;
    if (session.thesaurusDialogOpen) {
      event.preventDefault();
      session.thesaurusDialogOpen = false;
      return;
    }
    if (higherPriorityDismissOpen()) return;
    event.preventDefault();
    openThesaurusUi();
    return;
  }

  // Section jump (⌘1–5) — Writing / Storyboard / Scratchpad / Worldbuilding / Trash.
  if (
    mod &&
    event.shiftKey === false &&
    event.altKey === false &&
    key in SECTION_DIGIT_TABS
  ) {
    if (share.shareMode === true) return;
    if (higherPriorityDismissOpen()) return;
    event.preventDefault();
    void navigateToProjectTab(SECTION_DIGIT_TABS[key]!);
    return;
  }

  // Help → Keyboard Shortcuts (⌘/ / Ctrl+/).
  if (
    mod &&
    event.shiftKey === false &&
    event.altKey === false &&
    key === "/"
  ) {
    event.preventDefault();
    toggleKeyboardShortcuts();
    return;
  }

  if (mod === false) return;
  if (key === "s" && event.shiftKey === false) {
    event.preventDefault();
    void save.onSave();
  }
}

async function onBackToProjects(): Promise<void> {
  await save.flushSave();
  if (isTauri() === false && isCloudEditorHosted()) {
    window.location.href = cloudAccountUrl();
    return;
  }
  try {
    await navigateToSplash();
  } catch (err) {
    console.warn("navigateToSplash failed", err);
  }
}

const {
  ready,
  mobileShell,
  outlineOpen,
  contextPanelOpen,
  projectName,
  editorBottomInset,
  modLabel,
} = storeToRefs(session);

/** Right-rail occupants: storyboard / DB entry context, plus manuscript
 * scene details (Writing mode only). Keep aligned with the `context` named views. */
const hasContextPanelContent = computed(() => {
  return (
    route.name === "storyboard" ||
    route.name === "database-entry" ||
    (route.meta.section === "manuscript" && display.isWriting)
  );
});

const { activeSceneLabel } = useChapterHeadings();
const contextPanelToggleLabel = computed(() => {
  if (route.name === "storyboard") return "Scene links";
  if (route.name === "database-entry") return "Entry context";
  if (route.meta.section === "manuscript") return activeSceneLabel.value;
  return "Context";
});

/** Trash item selection must not remount the editor on every `/trash/<path>`
 * change. Preview is applied in-place via `showTrashPreview`; remounting on
 * `fullPath` raced that apply and left last-open scene chrome on screen. */
const editorSurfaceKey = computed(() => {
  const name = route.name;
  if (name === "trash" || name === "trash-item") return "trash";
  return route.fullPath;
});

const {
  isFocus: focusMode,
  isReading: readingMode,
  focusModeEligible,
  readingModeEligible,
} = storeToRefs(display);
const { toggleFocusMode, exitFocusMode, toggleReadingMode } = display;
const { shareMode } = storeToRefs(share);

const { exportBusy, exportBusyTitle, exportBusyMessage } = exportApi;
/** Always a Just so ZwDialog never sees a missing description prop. */
const exportDialogTitle = computed(() =>
  exportBusyTitle.value !== "" ? exportBusyTitle.value : "Exporting",
);
const exportDialogDescription = computed((): Maybe<string> =>
  just(
    exportBusyMessage.value !== "" ? exportBusyMessage.value : "Please wait.",
  ),
);
const { cloudSyncBusy, cloudSignedIn } = storeToRefs(cloud);

/** Shell root, measured so the layout follows the editor's own width rather
 * than the window (ZW-276). */
const shellRoot = useTemplateRef<HTMLElement>("shellRoot");
useShellWidth(
  () => toMaybe(shellRoot.value),
  (width) => session.measureShellWidth(width),
);

const showMobileNav = computed(
  () => mobileShell.value && focusMode.value === false,
);
const mobileNavTitle = computed(() =>
  projectName.value !== "" ? projectName.value : "Editor",
);
const showNewProjectMenuItem = computed(
  () => isTauri() && props.demo === false,
);
const showReadingModeMenuItem = computed(
  () => readingModeEligible.value && shareMode.value === false,
);
const showKeyboardShortcutsMenuItem = computed(() => shareMode.value === false);
/** Narrow More menu only; the wide web editor has no About entry (#350). */
const showAboutMenuItems = computed(() => shareMode.value === false);
/** The web app is always current, so web gets About but no update check. */
const showCheckUpdatesMenuItem = computed(
  () => showAboutMenuItems.value && isTauri(),
);
const cloudSyncMenuDisabled = computed(
  () => cloudSignedIn.value === false || cloudSyncBusy.value === true,
);
const showOutlineSurfaces = computed(() => focusMode.value === false);
const showEditorStatusBar = computed(
  () =>
    focusMode.value === false &&
    readingMode.value === false &&
    session.activeMap === false,
);
const newProjectCloseOnBackdrop = computed(
  () => creatingProjectBusy.value === false,
);
function toggleOutlineOpen(): void {
  outlineOpen.value = outlineOpen.value === false;
}

function toggleContextPanelOpen(): void {
  contextPanelOpen.value = contextPanelOpen.value === false;
}

const { onRequestSync } = cloud;

const { onSave } = save;

let unlistenDictionary: Maybe<() => void> = nothing;
let unlistenMenuCommands: Maybe<UnlistenFn> = nothing;
let unlistenCloudUpdated: Maybe<UnlistenFn> = nothing;
let stopHostedRealtime: Maybe<StopWatchHostedEditorTips> = nothing;

onMounted(async () => {
  void isMobileShell().then((mobileShell) =>
    dispatchShell({
      type: "About",
      msg: { type: "HostDetected", host: aboutHostFor(isTauri(), mobileShell) },
    }),
  );
  // Escape uses capture so focus mode still exits when the editor has focus.
  window.addEventListener("keydown", onKeydown, true);
  window.addEventListener("keydown", onKeydownBubble);
  window.addEventListener("blur", () => void save.onEditorBlur());
  unlistenDictionary = just(
    onProjectDictionaryChange((words) => {
      if (isNothing(session.projectConfig)) return;
      session.projectConfig = just({
        ...session.projectConfig.value,
        dictionary: words,
      });
    }),
  );

  if (props.demo) {
    try {
      // `?demo=1&legacy=1`: the pre-conversion Markdown project, converted on
      // open like a real one (#259).
      const payload = await withConvertedProject(
        await openDemoProject(
          route.query.legacy === "1" ? "markdown" : "zwdoc",
        ),
      );
      applyPayload(payload);
      await headings.refreshEntityOccurrences();
      session.activeCommentId = just("c_demo1");
    } catch (error) {
      console.error("Failed to load demo project", error);
      session.doc = "";
      session.ready = true;
    }
    return;
  }

  if (isTauri() === false) {
    const isEmbed =
      route.query.embed === "1" ||
      (typeof document !== "undefined" &&
        document.documentElement.dataset.zwEmbed === "1");

    if (isCloudReaderHosted()) {
      await share.syncFromHost();
      try {
        const payload = await openShareReaderProject();
        applyPayload(payload);
        if (isNothing(payload.activePath)) {
          const manuscript = isJust(payload.project)
            ? just(payload.project.value.manuscript)
            : nothing;
          const first = firstManuscriptScenePath(manuscript);
          if (isJust(first)) {
            await workspace.onSelectProjectFile(first.value);
          }
        }
        await headings.refreshEntityOccurrences();
        // No realtime watch for immutable public freezes.
      } catch (error) {
        // Do not bounce to /s/:token here. The reader HTML is only served after
        // the share gate already set the cookie; re-entering /s on invoke/session
        // failure just loops (/s → /reader → open fail → /s).
        console.error("Failed to load shared manuscript", error);
        session.doc = "";
        session.ready = true;
      }
      return;
    }

    const projectRoot = activeCloudProjectRoot();

    if (isNothing(projectRoot)) {
      // Marketing iframe without ?demo=1 still gets the sample project.
      if (isEmbed) {
        try {
          const payload = await openDemoProject();
          applyPayload(payload);
          await headings.refreshEntityOccurrences();
          session.activeCommentId = just("c_demo1");
        } catch (error) {
          console.error("Failed to load demo project", error);
          session.doc = "";
          session.ready = true;
        }
        return;
      }
      if (isCloudEditorHosted()) {
        window.location.href = cloudSignInUrl();
        return;
      }
      void router.replace("/splash");
      return;
    }

    try {
      await openCloudEditorProject(projectRoot.value);
      const payload = await withConvertedProject(await getEditorPayload());
      applyPayload(payload);
      await headings.refreshEntityOccurrences();
      startHostedRealtimeWatch();
    } catch (error) {
      console.error("Failed to load web project", error);
      session.doc = "";
      session.ready = true;
    }
    return;
  }

  session.nativeMobileShell = await isMobileShell();

  try {
    const viewTabHandlers = Object.fromEntries(
      Object.entries(VIEW_MENU_TABS).map(([id, tab]) => [
        id,
        () => {
          void navigateToProjectTab(tab);
        },
      ]),
    );
    unlistenMenuCommands = just(
      await listenMenuCommands({
        ...viewTabHandlers,
        "file-new": () => {
          matchMaybe(outlineActions.headerNew, {
            nothing: () => {},
            just: (action) => {
              action.run();
            },
          });
        },
        "file-new-project": () => openNewProjectDialog(),
        "file-save": () => void save.onSave(),
        "file-export-pdf": () => void exportApi.exportPdf(),
        "file-export-epub": () => void exportApi.exportEpub(),
        "file-export-docx": () => void exportApi.exportDocx(),
        "file-export-markdown": () => void exportApi.exportMarkdown(),
        "share-via-cloud": () => {
          void (async () => {
            const err = await openShareViaCloud(fromMaybe(session.rootPath));
            if (isJust(trimmedMaybe(err))) window.alert(err);
          })();
        },
        [EDIT_UNDO_MENU_ID]: () => runFocusedEditorHistory("undo"),
        [EDIT_REDO_MENU_ID]: () => runFocusedEditorHistory("redo"),
        "edit-find": () => chrome.openFind(),
        "edit-find-next": () => chrome.findNext(),
        "edit-select-next": () => chrome.selectNextOccurrence(),
        "edit-thesaurus": () => openThesaurusUi(),
        "view-search-project": () => {
          session.projectSearchOpen = true;
        },
        "view-focus-mode": () => toggleFocusModeGuarded(),
        "view-reading-mode": () => toggleReadingModeGuarded(),
        "help-keyboard-shortcuts": () => openKeyboardShortcuts(),
      }),
    );
    unlistenCloudUpdated = just(
      await listen<{ root: string }>("cloud-project-updated", (event) => {
        if (
          isNothing(session.rootPath) ||
          event.payload?.root !== session.rootPath.value
        ) {
          return;
        }
        void cloud.reloadAfterCloudUpdate();
      }),
    );
  } catch (error) {
    console.error("Failed to bind menu events", error);
  }

  try {
    const payload = await withConvertedProject(await getEditorPayload());
    // Apply the payload before replacing the route so project config is ready
    // when route sync loads the file for `lastRoute`.
    applyPayload(payload);
    if (isJust(payload.rootPath)) {
      const atEditorRoot =
        route.path === "/" &&
        (route.name === "manuscript" || isNothing(toMaybe(route.name)));
      if (atEditorRoot) {
        const saved = toMaybe(await readLastRoute(payload.rootPath.value));
        const target = matchMaybe(saved, {
          nothing: () => pathFromLegacyActiveTab(payload.activeTab),
          just: (path) =>
            path !== "/" ? path : pathFromLegacyActiveTab(payload.activeTab),
        });
        if (target !== "" && target !== "/") {
          await router.replace(target);
        }
      }
    }
    await headings.refreshEntityOccurrences();
    await cloud.pullCloudOnOpen();
    // A pull can bring Markdown another device wrote before converting.
    if (isJust(payload.rootPath)) {
      const converted = await withConvertedProject(
        await reloadEditorFromDisk(payload.rootPath.value),
      );
      if (converted !== payload) applyPayload(converted);
    }
    cloud.markRealtimeReady();
  } catch (error) {
    console.error("Failed to load editor payload", error);
    session.doc = "";
    session.ready = true;
  }
});

onUnmounted(() => {
  window.removeEventListener("keydown", onKeydown, true);
  window.removeEventListener("keydown", onKeydownBubble);
  matchMaybe(unlistenDictionary, {
    just: (fn) => {
      fn();
    },
    nothing: () => {},
  });
  unlistenDictionary = nothing;
  void save.flushSave();
  save.dispose();
  headings.dispose();
  cloud.dispose();
  matchMaybe(unlistenMenuCommands, {
    just: (fn) => {
      fn();
    },
    nothing: () => {},
  });
  unlistenMenuCommands = nothing;
  matchMaybe(unlistenCloudUpdated, {
    just: (fn) => {
      fn();
    },
    nothing: () => {},
  });
  matchMaybe(stopHostedRealtime, {
    just: (stop) => {
      stop();
    },
    nothing: () => {},
  });
  stopHostedRealtime = nothing;
});
</script>

<template>
  <div
    v-if="ready"
    ref="shellRoot"
    class="zw-editor-app h-full min-h-full"
    :class="{
      'zw-editor-app--mobile': mobileShell,
      'zw-editor-app--focus': focusMode,
      'zw-editor-app--reading': readingMode,
    }"
    :style="{
      background: 'var(--bg)',
      '--zw-editor-bottom-inset': `${editorBottomInset}px`,
    }"
  >
    <header v-if="showMobileNav" class="zw-mobile-nav">
      <button
        type="button"
        class="zw-mobile-nav-outline"
        aria-controls="zw-outline-drawer-left"
        :aria-expanded="outlineOpen"
        title="Outline"
        aria-label="Outline"
        @click="toggleOutlineOpen"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          class="size-4"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M4 6h16M4 12h10M4 18h14"
          />
        </svg>
      </button>
      <span class="zw-mobile-nav-title">{{ mobileNavTitle }}</span>
      <button
        v-if="hasContextPanelContent"
        type="button"
        class="zw-mobile-nav-context"
        aria-controls="zw-outline-drawer-bottom"
        :aria-expanded="contextPanelOpen"
        :title="contextPanelToggleLabel"
        @click="toggleContextPanelOpen"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          class="size-4"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"
          />
        </svg>
        <span class="zw-mobile-nav-context-label">Details</span>
      </button>
      <ZwMenu
        class="zw-mobile-nav-more"
        align="end"
        :menu-label="just('Editor actions')"
      >
        <template #trigger="{ open, toggle, attrs }">
          <button
            type="button"
            class="zw-mobile-nav-more-btn"
            v-bind="attrs"
            :aria-expanded="open"
            title="More"
            aria-label="More"
            @click="toggle()"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.5"
              class="size-4"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Zm0 6a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Zm0 6a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
              />
            </svg>
          </button>
        </template>
        <ZwMenuItem
          label="Save"
          :shortcut="nothing"
          :title="nothing"
          @select="onSave"
        />
        <ZwMenuItem
          v-if="showNewProjectMenuItem"
          label="New project"
          :shortcut="just(`${modLabel}⇧N`)"
          :title="nothing"
          @select="openNewProjectDialog"
        />
        <ZwMenuItem
          v-if="focusModeEligible"
          :label="focusMode ? 'Exit focus mode' : 'Focus mode'"
          :shortcut="nothing"
          :title="nothing"
          @select="toggleFocusMode"
        />
        <ZwMenuItem
          v-if="showReadingModeMenuItem"
          :label="readingMode ? 'Exit reading mode' : 'Reading mode'"
          :shortcut="nothing"
          :title="nothing"
          @select="toggleReadingMode"
        />
        <ZwMenuItem
          v-if="showKeyboardShortcutsMenuItem"
          label="Keyboard shortcuts"
          :shortcut="just(`${modLabel}/`)"
          :title="nothing"
          @select="openKeyboardShortcuts"
        />
        <ZwMenuItem
          label="Sync cloud backup"
          :disabled="cloudSyncMenuDisabled"
          :shortcut="nothing"
          :title="nothing"
          @select="onRequestSync"
        />
        <ZwMenuItem
          v-if="showAboutMenuItems"
          label="About Zephyrwrite"
          :shortcut="nothing"
          :title="nothing"
          @select="openAbout"
        />
        <ZwMenuItem
          v-if="showCheckUpdatesMenuItem"
          label="Check for updates"
          :shortcut="nothing"
          :title="nothing"
          @select="openAboutAndCheck"
        />
        <ZwMenuItem
          label="Return to projects"
          :shortcut="nothing"
          :title="nothing"
          @select="onBackToProjects"
        />
      </ZwMenu>
    </header>
    <ExportToast />
    <!-- Minimal exit affordance: reveal on top-edge hover so Focus isn't a trap. -->
    <div v-if="focusMode" class="zw-focus-exit-zone">
      <button
        type="button"
        class="zw-focus-exit"
        :title="`Exit focus (${modLabel}⇧F or Esc)`"
        @click="exitFocusMode"
      >
        Exit focus
      </button>
    </div>
    <div class="zw-editor-main">
      <div class="zw-editor-project-layout">
        <OutlineSurface
          v-if="showOutlineSurfaces"
          :mobile="mobileShell"
          :open="outlineOpen"
          side="left"
          label="Outline"
          @close="outlineOpen = false"
        >
          <EditorOutlinePane />
        </OutlineSurface>
        <div class="zw-editor-project-main">
          <router-view v-slot="{ Component }">
            <component :is="Component" :key="editorSurfaceKey" ref="editor" />
          </router-view>
        </div>
        <OutlineSurface
          v-if="showOutlineSurfaces"
          :mobile="mobileShell"
          :open="contextPanelOpen"
          :side="mobileShell ? 'bottom' : 'right'"
          :label="contextPanelToggleLabel"
          @close="contextPanelOpen = false"
        >
          <router-view name="context" />
        </OutlineSurface>
      </div>
    </div>
    <EditorStatusBar v-if="showEditorStatusBar" />
    <ProjectSearchDialog
      :open="session.projectSearchOpen"
      @close="session.projectSearchOpen = false"
    />
    <ThesaurusDialog
      :open="session.thesaurusDialogOpen"
      @close="session.thesaurusDialogOpen = false"
    />
    <KeyboardShortcutsDialog
      :open="shortcutsOpen"
      @close="shortcutsOpen = false"
    />
    <ZwDialog
      :open="aboutOpen"
      title="About Zephyrwrite"
      :description="nothing"
      :show-close="true"
      @close="closeAbout"
    >
      <AboutPanel host="dialog" :check-on-open="aboutCheckOnOpen" />
    </ZwDialog>
    <ZwDialog
      :open="exportBusy"
      :title="exportDialogTitle"
      :description="exportDialogDescription"
      :close-on-backdrop="false"
      @close="() => {}"
    >
      <p class="export-busy-status" role="status" aria-live="polite">
        Please wait…
      </p>
    </ZwDialog>
    <ZwDialog
      :open="creatingProject"
      title="New project"
      :description="just(newProjectDescription)"
      :close-on-backdrop="newProjectCloseOnBackdrop"
      @close="cancelNewProject"
    >
      <ZwInput
        :id="nothing"
        ref="newProjectInput"
        v-model="newProjectName"
        :label="just('Project name')"
        placeholder="Project name"
        :disabled="creatingProjectBusy"
        @keydown.enter.prevent="commitNewProject"
      />
      <p v-if="newProjectError.length > 0" class="zw-dialog-error">
        {{ newProjectError }}
      </p>
      <template #footer>
        <ZwButton :disabled="creatingProjectBusy" @click="cancelNewProject">
          Cancel
        </ZwButton>
        <ZwButton
          variant="primary"
          :disabled="creatingProjectBusy"
          @click="commitNewProject"
        >
          {{ newProjectPrimaryLabel }}
        </ZwButton>
      </template>
    </ZwDialog>
    <ZwContextMenuHost />
    <ProjectConversionNotice />
  </div>
</template>

<style scoped>
.export-busy-status {
  margin: 0;
  color: var(--text-muted);
  font-family: var(--font-ui);
  font-size: 0.9rem;
}

.zw-editor-app {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.zw-editor-app--mobile {
  height: 100%;
  /* Track the visible viewport so the soft keyboard does not hide the nav. */
  max-height: 100dvh;
}

.zw-mobile-nav {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 0 0 auto;
  padding: 0.65rem 0.85rem;
  padding-top: max(0.65rem, env(safe-area-inset-top, 0px));
  border-bottom: 1px solid var(--border, var(--line));
  background: var(--bg-raised);
  font-family: var(--font-ui);
}

.zw-mobile-nav-back {
  appearance: none;
  border: 1px solid var(--border, var(--line));
  background: var(--bg);
  color: var(--text);
  font: inherit;
  font-size: 0.85rem;
  padding: 0.35rem 0.65rem;
  cursor: pointer;
}

.zw-mobile-nav-back:hover {
  border-color: var(--text-muted);
}

.zw-mobile-nav-outline,
.zw-mobile-nav-context,
.zw-mobile-nav-more-btn {
  appearance: none;
  border: 1px solid var(--border, var(--line));
  background: var(--bg);
  color: var(--text);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.15rem;
  height: 2.15rem;
  padding: 0;
  cursor: pointer;
}

/* Labeled so Scene details stays discoverable in the narrow layout. */
.zw-mobile-nav-context {
  width: auto;
  gap: 0.35rem;
  padding: 0 0.65rem;
  font: inherit;
  font-size: 0.875rem;
  white-space: nowrap;
}

.zw-mobile-nav-outline:hover,
.zw-mobile-nav-outline[aria-expanded="true"],
.zw-mobile-nav-context:hover,
.zw-mobile-nav-context[aria-expanded="true"],
.zw-mobile-nav-more-btn:hover,
.zw-mobile-nav-more-btn[aria-expanded="true"] {
  border-color: var(--text-muted);
}

.zw-mobile-nav-title {
  min-width: 0;
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.85rem;
  color: var(--text-muted);
}

.zw-mobile-nav-more {
  flex: 0 0 auto;
  margin-left: auto;
}

.zw-editor-main {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
}

.zw-editor-project-layout {
  display: flex;
  height: 100%;
  min-height: 0;
}

.zw-editor-project-main {
  position: relative;
  flex: 1;
  min-width: 0;
  min-height: 0;
}

/* Leave room so the docked pill does not cover the last visible lines. */
.zw-editor-main:has(+ .zw-editor-status-dock) :deep(.zw-editor-scroll) {
  --zw-editor-dock-inset: 2.35rem;
}

.zw-focus-exit-zone {
  position: absolute;
  z-index: 30;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  padding-top: max(0.35rem, env(safe-area-inset-top, 0px));
  /* Tall hit/hover strip; button itself stays hidden until hover/focus. */
  height: 2.75rem;
  pointer-events: none;
}

.zw-focus-exit {
  pointer-events: auto;
  appearance: none;
  opacity: 0;
  border: 1px solid var(--line);
  border-radius: 0.35rem;
  background: var(--bg-raised);
  color: var(--text-muted);
  font-family: var(--font-ui);
  font-size: 0.78rem;
  padding: 0.3rem 0.65rem;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.zw-focus-exit-zone:hover .zw-focus-exit,
.zw-focus-exit:focus-visible {
  opacity: 1;
}

/* Touch shells have no hover — keep a quiet but discoverable exit. */
.zw-editor-app--mobile .zw-focus-exit {
  opacity: 0.9;
}

.zw-focus-exit:hover {
  color: var(--text);
  border-color: var(--text-muted);
}

.zw-focus-exit:focus-visible {
  outline: 2px solid var(--color-zw-amber);
  outline-offset: 2px;
}

/* A narrow web viewport needs a separate dock row so metrics cannot cover the
   outline's navigation. Native mobile keeps its keyboard-aware floating dock. */
@media (max-width: 600px) {
  .zw-editor-app:not(.zw-editor-app--mobile) :deep(.zw-editor-status-dock) {
    position: relative;
    flex-shrink: 0;
    max-width: 100%;
  }

  .zw-editor-app:not(.zw-editor-app--mobile) :deep(.zw-editor-status) {
    overflow-x: auto;
  }
}
</style>

<style>
html:is([data-zw-mobile-shell="1"], [data-zw-narrow="1"])
  .zw-editor-status-dock {
  /* Lift the dock clear of the soft keyboard and the home indicator; avoid
     padding-bottom (that left a hollow gap under the pill). The dock is
     `position: absolute` against .zw-editor-app, whose height follows the
     *layout* viewport — and Android doesn't shrink that for the IME — so the
     keyboard has to be added explicitly. See useKeyboardInset. */
  bottom: calc(
    var(--zw-keyboard-inset, 0px) + env(safe-area-inset-bottom, 0px)
  );
  padding-bottom: 0;
  /* Never wider than the editor, so a long scene title truncates instead of
     pushing the pill off the left edge. */
  max-width: 100%;
}
</style>
